import { useState, type ReactNode } from 'react'

export type TabDef = {
  id: string
  label: string
  badge?: number | string
  content: ReactNode
}

export function Tabs({
  tabs,
  defaultId,
  activeId,
  onChange,
}: {
  tabs: TabDef[]
  defaultId?: string
  activeId?: string
  onChange?: (tabId: string) => void
}) {
  const [internalActive, setInternalActive] = useState(activeId ?? defaultId ?? tabs[0]?.id)

  const active = activeId ?? internalActive
  const current = tabs.find((t) => t.id === active) ?? tabs[0]

  function handleChange(tabId: string) {
    if (activeId == null) {
      setInternalActive(tabId)
    }
    onChange?.(tabId)
  }

  return (
    <div>
      <div role="tablist" className="flex flex-wrap gap-1 rounded-lg border border-neutral-200 bg-white p-1 shadow-sm">
        {tabs.map((t) => {
          const isActive = t.id === active
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${t.id}`}
              id={`tab-${t.id}`}
              type="button"
              onClick={() => handleChange(t.id)}
              className={`rounded-md px-3 py-2 text-sm font-semibold transition focus-visible:outline-2 focus-visible:outline-accent-turquoise focus-visible:outline-offset-2 ${
                isActive
                  ? 'bg-secondary-lightest text-primary'
                  : 'text-neutral-500 hover:bg-neutral-50 hover:text-primary'
              }`}
            >
              {t.label}
              {t.badge != null && (
                <span
                  className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] px-1.5 h-5 rounded-full text-[11px] font-bold ${
                    isActive
                      ? 'bg-primary text-white'
                      : 'bg-neutral-100 text-neutral-600'
                  }`}
                >
                  {t.badge}
                </span>
              )}
            </button>
          )
        })}
      </div>
      <div
        id={`tabpanel-${current?.id}`}
        role="tabpanel"
        aria-labelledby={`tab-${current?.id}`}
        className="pt-4"
      >
        {current?.content}
      </div>
    </div>
  )
}
